import { useMemo } from 'react';
import { pick } from 'lodash-es';
import { useBackfillInProgress } from '@frontend/call-intel-backfill';
import { useAppScopeStore } from '@frontend/scope';
import { BASE_FILTER_KEYS } from '../constants';
import { useCallIntelShallowStore } from './use-call-intel-store';

const EMPTY_LOCATIONS: string[] = [];

export const useActiveFilters = () => {
  const { selectedLocationIds } = useAppScopeStore();
  const { pendingBatch1LocationIds } = useBackfillInProgress({ locationIds: selectedLocationIds });

  const {
    filters: globalFilters,
    filterHintText,
    subViewFilters,
    subView,
    urlOverrideFilters,
    updateFilters: updateGlobalFilters,
    updateSubViewFilters,
    resetFilters: resetGlobalFilters,
    resetSubViewFilters,
    applyUrlOverrideFilters,
    clearUrlOverrideFilters,
  } = useCallIntelShallowStore(
    'filters',
    'filterHintText',
    'subViewFilters',
    'subView',
    'urlOverrideFilters',
    'updateFilters',
    'updateSubViewFilters',
    'resetFilters',
    'resetSubViewFilters',
    'applyUrlOverrideFilters',
    'clearUrlOverrideFilters'
  );

  const isSubView = !!subView.id;
  const hasUrlOverride = useMemo(() => Object.keys(urlOverrideFilters).length > 0, [urlOverrideFilters]);

  const activeFilters = hasUrlOverride ? urlOverrideFilters : isSubView ? subViewFilters : globalFilters;

  // `filters` is actively sanitized to strip out any Batch-1 pending locations.
  // This version of the filters must be used in all API queries to avoid sending pending/empty locations to the backend.
  const filters = useMemo(() => {
    if (!activeFilters.locations || pendingBatch1LocationIds.length === 0) {
      return activeFilters;
    }
    const filteredLocations = activeFilters.locations.filter((id) => !pendingBatch1LocationIds.includes(id));
    if (filteredLocations.length !== activeFilters.locations.length) {
      return { ...activeFilters, locations: filteredLocations };
    }
    return activeFilters;
  }, [activeFilters, pendingBatch1LocationIds]);

  const baseFilters = pick(filters, BASE_FILTER_KEYS);

  const updateFilterValues = hasUrlOverride
    ? applyUrlOverrideFilters
    : isSubView
    ? updateSubViewFilters
    : updateGlobalFilters;

  return {
    filters,
    // `allSelectedLocationIds` retains the raw, unfiltered location selections (including locations undergoing backfill).
    // This is used strictly in UI selection controls (like the location selector dropdown) to ensure checkboxes
    // and labels remain accurate, even if some selected locations are pending/empty in backend queries.
    allSelectedLocationIds: activeFilters.locations || EMPTY_LOCATIONS,
    filterHintText,
    updateFilterValues,
    isSubView,
    hasUrlOverride,
    resetFilters: isSubView ? resetSubViewFilters : resetGlobalFilters,
    clearUrlOverrideFilters,
    globalFilters,
    subViewFilters,
    baseFilters,
  };
};
